/**
 * Стан очікування маршруту для `<RoutePending>` (ui/RoutePending.tsx; stability.md, розд. 2): шкала
 * від початку переходу (роутер уже відчекав `defaultPendingMs`), повтор — нові запити й
 * `router.invalidate()`.
 */
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { AreaState } from '../ui/Area.tsx';
import { usePendingSince } from '../ui/RoutePending.tsx';

/**
 * Скасувати запити в дорозі, на які ніхто не дивиться (запити `loader`-а маршруту), щоб повтор
 * надіслав їх знову. Запити змонтованих областей (є спостерігачі) не чіпаємо. Синхронно: новий
 * `fetch` того самого ключа після цього вже не приєднується до скасованого.
 */
export function cancelOrphanFetches(client: QueryClient): void {
  void client.cancelQueries(
    { predicate: (q) => q.state.fetchStatus === 'fetching' && q.getObserversCount() === 0 },
    { silent: true },
  );
}

/** Стан очікування маршруту: від початку переходу, повтор — нові запити й `router.invalidate()`. */
export function useRoutePending(): AreaState<never> {
  const router = useRouter();
  const client = useQueryClient();
  return usePendingSince({
    waitedMs: router.options.defaultPendingMs ?? 0,
    onRetry: () => {
      cancelOrphanFetches(client);
      void router.invalidate();
    },
  });
}
